import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import ImageUploader from '../../components/common/ImageUploader';
import LivePreviewContainer from '../../components/dashboard/LivePreviewContainer';
import { FiLock, FiSliders, FiSave } from 'react-icons/fi';

const THEMES = [
  { id: 'minimal-white', name: 'Minimal Pearl White', desc: 'Clean, crisp typography with light aesthetics.', bg: '#FFFFFF', accent: '#18181B', pro: false },
  { id: 'dark-premium', name: 'Dark Cyber Premium', desc: 'Sleek dark mode with glowing cyan and indigo accents.', bg: '#090D16', accent: '#6366F1', pro: true },
  { id: 'elegant', name: 'Royal Luxury Serif', desc: 'Warm luxury tones with refined serif typography.', bg: '#FAF6F0', accent: '#8B4513', pro: true },
  { id: 'modern-business', name: 'Modern Tech Business', desc: 'Deep corporate header gradient with crisp product cards.', bg: '#F1F5F9', accent: '#2563EB', pro: true },
  { id: 'colorful', name: 'Vibrant Neon Pop', desc: 'Punchy header gradients and energetic category badges.', bg: '#FFF7ED', accent: '#EA580C', pro: true }
];

const FONTS = [
  { id: 'sans', name: 'Modern Sans (Inter)' },
  { id: 'outfit', name: 'Brand Heading (Outfit)' },
  { id: 'serif', name: 'Luxury Serif (Playfair)' }
];

const CARD_STYLES = [
  { id: 'glass', name: 'Glassmorphism Blur' },
  { id: 'elevated', name: 'Elevated Soft Shadow' },
  { id: 'outline', name: 'Crisp Border Outline' },
  { id: 'flat', name: 'Flat Solid Rounded' }
];

const ThemesPage = () => {
  const { business, updateBusinessState } = useAuth();
  const { showToast } = useToast();

  const [form, setForm] = useState({
    theme: 'dark-premium',
    primaryColor: '#6366F1',
    secondaryColor: '#06B6D4',
    fontStyle: 'sans',
    cardStyle: 'glass',
    coverImage: ''
  });

  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (business) {
      setForm({
        theme: business.theme || 'dark-premium',
        primaryColor: business.primaryColor || '#6366F1',
        secondaryColor: business.secondaryColor || '#06B6D4',
        fontStyle: business.fontStyle || 'sans',
        cardStyle: business.cardStyle || 'glass',
        coverImage: business.coverImage || ''
      });
    }
    api.get('/categories').then((res) => setCategories(res.data)).catch(() => {});
    api.get('/products').then((res) => setProducts(res.data)).catch(() => {});
  }, [business]);

  const handleSaveTheme = async (updatedFields = {}) => {
    const payload = { ...form, ...updatedFields };

    if (business?.plan === 'FREE' && payload.theme !== 'minimal-white') {
      showToast('This premium theme is available on BASIC and PRO plans. Upgrade your plan to unlock!', 'error');
      return;
    }

    setSaving(true);
    try {
      const res = await api.put('/business', payload);
      if (res.success) {
        updateBusinessState(res.data);
        showToast('Theme & Style Customizations saved!', 'success');
      }
    } catch (err) {
      showToast(err.message || 'Failed to update theme', 'error');
    } finally {
      setSaving(false);
    }
  };

  const previewBizObj = {
    ...business,
    ...form
  };

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.75rem', fontWeight: 800 }}>
          Themes & Visual Customizer
        </h1>
        <p style={{ color: '#94A3B8', fontSize: '0.95rem' }}>
          Customize your page template, brand colors, typography, card shapes, and cover banner with instant live preview.
        </p>
      </div>

      <div className="split-editor-layout">
        <div>
          <div className="glass-card" style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.15rem', fontWeight: 700, marginBottom: '1.25rem' }}>
              Select Page Template Theme
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
              {THEMES.map((t) => {
                const isSelected = form.theme === t.id;
                const isLocked = business?.plan === 'FREE' && t.pro;

                return (
                  <div
                    key={t.id}
                    onClick={() => {
                      setForm((prev) => ({ ...prev, theme: t.id }));
                      handleSaveTheme({ theme: t.id });
                    }}
                    style={{
                      background: '#1E293B',
                      borderRadius: '16px',
                      padding: '1.25rem',
                      border: isSelected ? '2px solid var(--primary-500)' : '1px solid rgba(255,255,255,0.08)',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    <div style={{ height: '70px', borderRadius: '10px', background: t.bg, border: '1px solid rgba(0,0,0,0.1)', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: t.accent }} />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: '700', color: '#fff' }}>{t.name}</h4>
                      {isSelected && <span className="badge badge-success">Active</span>}
                      {isLocked && (
                        <span style={{ color: '#F59E0B', fontSize: '0.75rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <FiLock /> PRO
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#94A3B8', marginTop: '0.35rem' }}>{t.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="glass-card">
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.15rem', fontWeight: 700, marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FiSliders style={{ color: 'var(--primary-500)' }} /> Style Controls & Brand Customizer
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
              <div className="form-group">
                <label className="form-label">Primary Brand Color</label>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <input
                    type="color"
                    style={{ width: '48px', height: '42px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: 'none' }}
                    value={form.primaryColor}
                    onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-control"
                    value={form.primaryColor}
                    onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Secondary Accent Color</label>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <input
                    type="color"
                    style={{ width: '48px', height: '42px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: 'none' }}
                    value={form.secondaryColor}
                    onChange={(e) => setForm({ ...form, secondaryColor: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-control"
                    value={form.secondaryColor}
                    onChange={(e) => setForm({ ...form, secondaryColor: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
              <div className="form-group">
                <label className="form-label">Typography Style</label>
                <select
                  className="form-control"
                  value={form.fontStyle}
                  onChange={(e) => setForm({ ...form, fontStyle: e.target.value })}
                >
                  {FONTS.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Product Card Shape / Border Style</label>
                <select
                  className="form-control"
                  value={form.cardStyle}
                  onChange={(e) => setForm({ ...form, cardStyle: e.target.value })}
                >
                  {CARD_STYLES.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <ImageUploader
              label="Cover Banner Background (Optional)"
              value={form.coverImage}
              onChange={(url) => setForm({ ...form, coverImage: url })}
            />

            <button
              onClick={() => handleSaveTheme()}
              disabled={saving}
              className="btn btn-primary btn-lg"
              style={{ width: '100%', marginTop: '1.5rem' }}
            >
              {saving ? 'Saving Customizations...' : 'Save Theme & Style Changes'} <FiSave />
            </button>
          </div>
        </div>

        <LivePreviewContainer
          business={previewBizObj}
          categories={categories}
          products={products}
        />
      </div>
    </div>
  );
};

export default ThemesPage;
